/* eslint-disable jsx-a11y/alt-text */
import { XIcon } from "@heroicons/react/outline";
import { ExclamationIcon } from "@heroicons/react/solid";
import { Box, Divider, LinearProgress } from "@mui/material";
import { formatDisplayDate, formatTimestamp } from "Helper";
import { useEffect, useState, useRef } from "react";
import alarmSound from "Assets/audio/alarm.mp3";
import { decryptNik } from "Config/cripto";

function DpoAlert({ onClose, index, data, setIsPaused, isPaused }) {
  const [progress, setProgress] = useState(0);
  const [isSoundOn, setIsSoundOn] = useState(true); // Untuk mengontrol suara alarm
  const startTimeRef = useRef(Date.now());
  const audioRef = useRef(null);
  const isPausedRef = useRef(isPaused);
  const duration = 2000; // Durasi 2 detik

  // Fungsi untuk memutar/mematikan suara
  const toggleSound = () => {
    setIsSoundOn(!isSoundOn);
  };

  // Fungsi untuk memulai alarm
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(alarmSound); // Pastikan path audio benar
      audioRef.current.loop = true; // Suara alarm berulang
    }

    // Mengontrol pemutaran suara
    if (isSoundOn) {
      audioRef.current
        .play()
        .catch((err) => console.log("Audio play failed:", err));
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset posisi suara ke awal
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [isSoundOn]);

  // Fungsi untuk menghitung progress
  const startProgress = () => {
    const animateProgress = () => {
      if (!isPausedRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        const newProgress = Math.min((elapsed / duration) * 100, 100); // Hitung progres

        setProgress(newProgress); // Perbarui state progres

        if (newProgress < 100) {
          requestAnimationFrame(animateProgress); // Lanjutkan animasi
        } else {
          onClose(index); // Tutup alert setelah progres selesai
        }
      }
    };

    requestAnimationFrame(animateProgress); // Mulai animasi progres
  };

  useEffect(() => {
    startProgress();
  }, [index]);

  useEffect(() => {
    isPausedRef.current = isPaused; // Sinkronkan ref dengan state isPaused
  }, [isPaused]);

  // Fungsi untuk handle mouse enter dan leave
  const handleMouseEnter = () => {
    setIsPaused(true);
    isPausedRef.current = true;
    // Pause suara saat mouse masuk
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset suara ke awal
    }
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    isPausedRef.current = false;
    startTimeRef.current = Date.now() - (progress / 100) * duration; // Reset waktu
    startProgress(); // Mulai ulang progres

    // Play suara lagi saat mouse keluar
    if (isSoundOn && audioRef.current) {
      audioRef.current
        .play()
        .catch((err) => console.log("Audio play failed:", err));
    }
  };

  return (
    <div
      className="fixed inset-0 flex justify-center items-center p-10"
      style={{ zIndex: `${46 - index}` }}
    >
      <div
        className="w-full sm:w-3/4 md:max-w-2xl bg-black min-h-[100px] rounded-lg overflow-hidden cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header */}
        <div className="animate-blink-black-red text-white px-3 py-2 font-semibold flex justify-between">
          <div className="text-lg font-black flex items-center gap-2">
            <ExclamationIcon height="1.5rem" />
            TARGET DETECTED
          </div>
          <button
            type="button"
            className="rounded-md text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => onClose(index)}
            aria-label="Close alert"
          >
            <span className="sr-only">Close</span>
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="w-full grid grid-cols-2 gap-5 p-5">
          <div className="w-[300px] relative">
            <img
              src={data?.personFaceRes}
              className="object-cover rounded-lg"
              style={{ width: "300px", height: "300px" }}
            />
            {/* <div className="absolute top-0 left-0 h-4 w-4 border-t-2 border-l-2 border-red-500"></div>
            <div className="absolute top-0 right-0 h-4 w-4 border-t-2 border-r-2 border-red-500"></div>
            <div className="absolute -bottom-0 left-0 h-4 w-4 border-b-2 border-l-2 border-red-500"></div>
            <div className="absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-red-500"></div> */}
          </div>
          <div className="text-white text-sm flex flex-col gap-2">
            <label className="flex justify-between">
              <p className="font-black text-sm">Nama :</p>
              <div className="text-sm">{data?.fullName}</div>
            </label>
            <label className="flex justify-between">
              <p className="font-black text-sm">NIK :</p>
              <div className="text-sm">{decryptNik(data?.nik)}</div>
            </label>
            <label className="flex justify-between">
              <p className="font-black text-sm">TTL :</p>
              <div className="text-sm">
                {data?.placeOfBirth +
                  ", " +
                  (formatDisplayDate(data?.dateOfBirth) !== "Invalid date"
                    ? formatDisplayDate(data?.dateOfBirth)
                    : data?.dateOfBirth)}
              </div>
            </label>
            <label className="flex justify-between">
              <p className="font-black text-sm">Alamat :</p>
              <div className="text-sm">{data?.placeOfBirth}</div>
            </label>
            <Divider sx={{ backgroundColor: "white", height: "2px" }} />
            <label className="flex justify-between">
              <p className="font-black text-sm">Terdeteksi Pada :</p>
              <div className="text-sm">{formatTimestamp(data?.timestamp)}</div>
            </label>
          </div>
        </div>
        <Box sx={{ width: "100%" }}>
          <LinearProgress
            variant="determinate"
            value={progress} // Gunakan state progress
            color="error"
          />
        </Box>
        <button onClick={toggleSound} className="text-white mt-2">
          {isSoundOn ? "Mute Sound" : "Unmute Sound"}
        </button>
      </div>
    </div>
  );
}

export default DpoAlert;
